import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

/** Catches render-time errors in a subtree so a malformed message or broken
 *  child can't blank out an entire page. Shows a friendly fallback with the
 *  error excerpt + a "Try again" button that re-mounts the children. */
export default class ErrorBoundary extends React.Component {
  state = { error: null, info: null, key: 0 };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    if (typeof window !== 'undefined' && window.console) {
      // eslint-disable-next-line no-console
      console.error('[ErrorBoundary]', error, info?.componentStack);
    }
  }

  retry = () => {
    this.setState((s) => ({ error: null, info: null, key: s.key + 1 }));
  };

  render() {
    const { error, key } = this.state;
    if (!error) {
      return <React.Fragment key={key}>{this.props.children}</React.Fragment>;
    }
    return (
      <div className="fab-card p-6 max-w-xl mx-auto mt-10 border-l-4 border-fab-warning" data-testid="error-boundary-fallback">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 text-fab-warning shrink-0" />
          <div className="flex-1">
            <div className="font-display font-bold text-fab-text">Something rendered incorrectly</div>
            <div className="text-[12px] text-fab-text-secondary mt-1">
              The page hit an unexpected error and stopped rendering this section. Your data is safe — backend activity continues normally. Click below to retry.
            </div>
            <details className="mt-3 text-[11px] text-fab-text-muted">
              <summary className="cursor-pointer hover:text-fab-text-secondary">Technical details</summary>
              <pre className="mt-1 p-2 bg-fab-elevated rounded font-mono text-[10px] overflow-x-auto">{String(error?.message || error)}</pre>
            </details>
            <button
              onClick={this.retry}
              data-testid="error-retry"
              className="mt-4 btn-primary text-[12px] flex items-center gap-1.5"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Try again
            </button>
          </div>
        </div>
      </div>
    );
  }
}
